import MainTopBar from '../../ui/panels/TopBars/MainTopBar/MainTopBar';
import { useState, useEffect } from 'react';
import { LoginState } from '../../types/LoginState';
import { Link, useNavigate } from 'react-router-dom';
import { UserUtils } from '../../utils/UserUtils';
import './CitationPage.css';
import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import EditableSymptom from '../../ui/components/EditableSymptom/EditableSymptom';
import { CitationItem, SymptomCitations } from '../../types/Citations';

interface UserDetailObject {
  userName: string;
  id: string;
  email: string;
  role: string;
}
interface CaseDetails {
  status: string;
  email: string;
  firstName: string;
  entityId: string;
  lastName: string;
}

interface CitationStatus {
  status: string;
  error: string;
}

interface LetterStatus {
  status: string;
  error: string;
}

interface PatientDetails {
  case: CaseDetails;
  citationStatus: CitationStatus;
  letterStatus: LetterStatus;
}

function CitationPage(): JSX.Element {
  const [token, setToken] = useState<string>(
    localStorage.getItem('token') || ''
  );
  const nav = useNavigate();
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });
  const [userDetails, setUserDetails] = useState<UserDetailObject>({
    userName: '',
    id: '',
    email: '',
    role: '',
  });
  const [patientDetails, setPatientDetails] = useState<PatientDetails>({
    case: { status: '', email: '', firstName: '', entityId: '', lastName: '' },
    citationStatus: { status: 'unknown', error: '' },
    letterStatus: { status: 'unknown', error: '' },
  });
  const [symptomCitations, setSymptomCitations] = useState<SymptomCitations[]>(
    []
  );
  const { userId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (token == '') {
      nav('/signin');
    }
    const userDetails = UserUtils.decodeUserToken(token);
    if (!userDetails) {
      nav('/signin');
    } else {
      setUserDetails(userDetails);
    }
  }, [token]);

  useEffect(() => {
    const fetchPatientAndCitations = async () => {
      if (!userId || !token) return;
      try {
        // fetch patient entity access to get entityId
        const patientRes = await fetch(
          `/admin/caseDetails/${userId}?detailed=false`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!patientRes.ok) {
          console.log(`Error occurred at patient res`);
          // TODO proper error handling here
          return;
        }

        const pdata: PatientDetails = await patientRes.json();
        setPatientDetails(pdata);
        const entityId = pdata.case.entityId;
        const citationsRes = await fetch(`/citations/by-entity/${entityId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!citationsRes.ok) {
          console.log(`Error occurred at citaiton response`);
          // TODO proper error handling here
          return;
        }
        const citationResponse = await citationsRes.json();
        const cdata: SymptomCitations[] = citationResponse;
        if (
          cdata.length > 0 &&
          cdata[0].citations &&
          cdata[0].citations.length > 0
        ) {
          setSymptomCitations(cdata);
        } else {
          setSymptomCitations([]);
        }
      } catch (e) {
        console.error('Failed to load citations', e);
      }
      setLoading(false);
    };
    fetchPatientAndCitations();
  }, [userDetails.id, token]);

  const handleOpenFormPage = () => {
    nav(`/questions/${userId}`);
  };

  const handleEditCitation = async (
    symptomId: number,
    citation: CitationItem
  ) => {
    setLoading(true);
    try {
      const jsonData = {
        citationId: citation.citationId,
        citation: citation.citation,
        rationale: citation.rationale,
      };
      const response = await fetch(`/admin/citations/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });
      if (!response.ok) {
        // put error case here
        setLoading(false);
        return false;
      }
      setSymptomCitations((prev) =>
        prev.map((symptom) => {
          if (symptom.symptomId !== symptomId) return symptom;
          return {
            ...symptom,
            citations: symptom.citations.map((c) =>
              c.citationId === citation.citationId ? { ...c, ...citation } : c
            ),
          };
        })
      );
      return true;
    } catch (error) {}
    setLoading(false);
    return false;
  };

  const handleAddCitation = async (
    symptomId: number,
    citation: CitationItem
  ) => {
    setLoading(true);
    try {
      const jsonData = {
        symptomId: symptomId,
        citation: citation.citation,
        rationale: citation.rationale,
      };
      const response = await fetch(`/admin/citations/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });
      if (!response.ok) {
        // put error case here
        setLoading(false);
        return false;
      }
      const newCitation: CitationItem = await response.json();
      setSymptomCitations((prev) =>
        prev.map((symptom) => {
          if (symptom.symptomId !== symptomId) return symptom;
          return {
            ...symptom,
            citations: [...symptom.citations, newCitation],
          };
        })
      );
      setLoading(false);
      return true;
    } catch (error) {}
    setLoading(false);
    return false;
  };

  const handleRejectCitation = async (
    symptomId: number,
    citation: CitationItem
  ) => {
    setLoading(true);
    try {
      const jsonData = {
        citationId: citation.citationId,
        rejectionReason: citation.rejectionReason,
      };
      const response = await fetch(`/admin/citations/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });
      if (!response.ok) {
        // put error case here
        setLoading(false);
        return;
      }
      setSymptomCitations((prev) =>
        prev.map((symptom) => {
          if (symptom.symptomId !== symptomId) return symptom;

          return {
            ...symptom,
            citations: symptom.citations.filter(
              (c) => c.citationId !== citation.citationId
            ),
          };
        })
      );
    } catch (error) {}
    setLoading(false);
  };

  return (
    <div className="citation-page-container">
      <MainTopBar
        isLoggedIn={loginState.isLoggedIn}
        isSticky={true}
        showAdminButton={loginState.isAdmin}
        userName={userDetails.userName}
      />
      <div className="patientDetailsContainer">
        <h1>Welcome to Scribe</h1>
        <div className="patientDetails">
          <p>
            Patient Name :{' '}
            <span>
              {patientDetails.case.firstName} {patientDetails.case.lastName}
            </span>{' '}
          </p>
          <p>
            Status :{' '}
            <span className="patientStatus">{patientDetails.case.status}</span>{' '}
            {/* // TODO: hardcoded status for now. Waiting on Umair's Patient detail API */}
          </p>
        </div>
      </div>

      {/* // TODO:Following is a placeholder for citations. Need API to integrate with backend. */}
      <div className="citation-section">
        <button
          className="citation-back-btn"
          onClick={() => handleOpenFormPage()}
        >
          <MdOutlineKeyboardArrowLeft /> Back to Questions
        </button>
        <div className="citation-box">
          <div className="citation-head">
            <h2>Citations</h2>
            {symptomCitations.length > 0 &&
              symptomCitations[0].citations.length > 0 && (
                <p>Last Update: {symptomCitations[0].citations[0].dateAdded}</p>
              )}
          </div>
          {/* Dynamic citation list: each block shows symptom as h5 and its rationales as p */}
          <div className="citation-body">
            {symptomCitations.map((item) => (
              <div key={item.symptomId} className="citation-study">
                <EditableSymptom
                  symptom={item}
                  onUpdateCitation={handleEditCitation}
                  onAddCitation={handleAddCitation}
                  onRejectCitation={handleRejectCitation}
                />
              </div>
            ))}
          </div>

          <div className="citation-decide-btns">
            <button className="citation-decide-reject">REJECT</button>
            <button className="citation-decide-approve">APPROVE</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CitationPage;
